import type { UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import axios from 'axios';

import type { StatsMetricValue, StatsResponseType } from './types';

const endpoint = process.env.NEXT_PUBLIC_STATS_API ?? '';

type ResultType = {
  ferroCro: StatsMetricValue[];
  ferroAtom: StatsMetricValue[];
  vvsCro: StatsMetricValue[];
  tectonicCro: StatsMetricValue[];
  vvsVno: StatsMetricValue[];
};

async function fetchMetrics(
  startTimestamp: number,
  endTimestamp: number,
): Promise<ResultType> {
  const response: AxiosResponse<StatsResponseType> = await axios.post(
    endpoint,
    {
      endpointUrl: 'insights/cronos/veno/query',
      timeRange: {
        start: startTimestamp.toString(),
        end: endTimestamp.toString(),
        step: 86400,
      },
      queries: [
        {
          metricsQuery: {
            query: 'deposit_counter',
            alias: '',
            id: 'a',
            labelSelector: {
              contract_name: 'Venostorm',
              pid: '1',
            },
            aggregate: null,
            functions: [],
            disabled: true,
          },
          dataSource: 'METRICS',
          sourceName: '',
        },
        {
          metricsQuery: {
            query: 'withdraw_counter',
            alias: '',
            id: 'b',
            labelSelector: {
              contract_name: 'Venostorm',
              pid: '1',
            },
            aggregate: null,
            functions: [],
            disabled: true,
          },
          dataSource: 'METRICS',
          sourceName: '',
        },
        {
          priceQuery: {
            id: 'c',
            alias: '',
            coinId: [
              {
                symbol: 'CRO',
              },
              {
                symbol: 'LCRO',
              },
            ],
            disabled: true,
          },
          dataSource: 'PRICE',
          sourceName: '',
        },
        {
          metricsQuery: {
            query: 'deposit_counter',
            alias: '',
            id: 'd',
            labelSelector: {
              contract_name: 'Venostorm',
              pid: '2',
            },
            aggregate: null,
            functions: [],
            disabled: true,
          },
          dataSource: 'METRICS',
          sourceName: '',
        },
        {
          metricsQuery: {
            query: 'withdraw_counter',
            alias: '',
            id: 'e',
            labelSelector: {
              contract_name: 'Venostorm',
              pid: '2',
            },
            aggregate: null,
            functions: [],
            disabled: true,
          },
          dataSource: 'METRICS',
          sourceName: '',
        },
        {
          priceQuery: {
            id: 'f',
            alias: '',
            coinId: [],
            disabled: true,
          },
          dataSource: 'PRICE',
          sourceName: '',
        },
        {
          metricsQuery: {
            query: 'deposit_counter',
            alias: '',
            id: 'g',
            labelSelector: {
              contract_name: 'Venostorm',
              pid: '3',
            },
            aggregate: null,
            functions: [],
            disabled: true,
          },
          dataSource: 'METRICS',
          sourceName: '',
        },
        {
          metricsQuery: {
            query: 'withdraw_counter',
            alias: '',
            id: 'h',
            labelSelector: {
              contract_name: 'Venostorm',
              pid: '3',
            },
            aggregate: null,
            functions: [],
            disabled: true,
          },
          dataSource: 'METRICS',
          sourceName: '',
        },
        {
          metricsQuery: {
            query: 'deposit_counter',
            alias: '',
            id: 'i',
            labelSelector: {
              contract_name: 'Venostorm',
              pid: '4',
            },
            aggregate: null,
            functions: [],
            disabled: true,
          },
          dataSource: 'METRICS',
          sourceName: '',
        },
        {
          metricsQuery: {
            query: 'withdraw_counter',
            alias: '',
            id: 'j',
            labelSelector: {
              contract_name: 'Venostorm',
              pid: '4',
            },
            aggregate: null,
            functions: [],
            disabled: true,
          },
          dataSource: 'METRICS',
          sourceName: '',
        },
        {
          priceQuery: {
            id: 'k',
            alias: '',
            coinId: [
              {
                symbol: 'ATOM',
              },
              {
                symbol: 'LATOM',
              },
            ],
            disabled: true,
          },
          dataSource: 'PRICE',
          sourceName: '',
        },
        {
          priceQuery: {
            id: 'l',
            alias: '',
            coinId: [
              {
                symbol: 'CRO',
              },
            ],
            disabled: true,
          },
          dataSource: 'PRICE',
          sourceName: '',
        },
        {
          metricsQuery: {
            query: 'deposit_counter',
            alias: '',
            id: 'm',
            labelSelector: {
              contract_name: 'Venostorm',
              pid: '5',
            },
            aggregate: null,
            functions: [],
            disabled: true,
          },
          dataSource: 'METRICS',
          sourceName: '',
        },
        {
          metricsQuery: {
            query: 'withdraw_counter',
            alias: '',
            id: 'n',
            labelSelector: {
              contract_name: 'Venostorm',
              pid: '5',
            },
            aggregate: null,
            functions: [],
            disabled: true,
          },
          dataSource: 'METRICS',
          sourceName: '',
        },
      ],
      formulas: [
        {
          expression: 'sum(a-b)',
          alias: 'Ferro (CRO)',
          id: 'A',
          disabled: false,
          functions: [],
        },
        {
          expression: 'sum(g-h)',
          alias: 'Tectonic (CRO)',
          id: 'B',
          disabled: false,
          functions: [],
        },
        {
          expression: 'sum(d-e)',
          alias: 'VVS (VNO)',
          id: 'C',
          disabled: false,
          functions: [],
        },
        {
          expression: 'sum(i-j)',
          alias: 'Ferro (ATOM) ',
          id: 'D',
          disabled: false,
          functions: [],
        },
        {
          expression: 'sum(m-n)',
          alias: 'VVS (CRO)',
          id: 'E',
          disabled: false,
          functions: [],
        },
      ],
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  const results = response.data.results;

  if (results.length < 5) {
    console.error('invalid response from API for useStatsTvlTokens');
  }

  const ferroCro =
    results
      .find((r) => r.id === 'A')
      // bug in data returned from sentio, need to filter to make sure about end timestamp
      ?.matrix?.samples[0].values.filter(
        (v) => Number(v.timestamp) <= endTimestamp,
      ) ?? [];
  const tectonicCro =
    results
      .find((r) => r.id === 'B')
      ?.matrix?.samples[0].values.filter(
        (v) => Number(v.timestamp) <= endTimestamp,
      ) ?? [];
  const vvsVno =
    results
      .find((r) => r.id === 'C')
      ?.matrix?.samples[0].values.filter(
        (v) => Number(v.timestamp) <= endTimestamp,
      ) ?? [];
  const ferroAtom =
    results
      .find((r) => r.id === 'D')
      ?.matrix?.samples[0].values.filter(
        (v) => Number(v.timestamp) <= endTimestamp,
      ) ?? [];
  const vvsCro =
    results
      .find((r) => r.id === 'E')
      ?.matrix?.samples[0].values.filter(
        (v) => Number(v.timestamp) <= endTimestamp,
      ) ?? [];

  return { ferroCro, tectonicCro, ferroAtom, vvsCro, vvsVno };
}

export const useStatsTvlTokens = (
  startTimestamp: number,
  endTimestamp: number,
  options?: UseQueryOptions<ResultType>,
): UseQueryResult<ResultType, unknown> => {
  const queryKey = ['useStatsTvlTokens', startTimestamp, endTimestamp];

  return useQuery({
    queryKey,
    queryFn: () => fetchMetrics(startTimestamp, endTimestamp),
    ...options,
  });
};
